
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FilterCategory } from './types';
import { Sparkles, Map, Filter as FilterIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AestheticFilterProps {
  filteredDevicesCount: number;
  consumersCount: number;
  showDevices: boolean;
  showConsumers: boolean;
  setShowDevices: (show: boolean) => void;
  setShowConsumers: (show: boolean) => void;
  filterCategories: FilterCategory[];
  activeFilters: Record<string, string[]>;
  onFilterChange: (category: string, value: string) => void;
  onClearFilters: () => void;
}

const AestheticFilter: React.FC<AestheticFilterProps> = ({
  filteredDevicesCount,
  consumersCount,
  showDevices,
  showConsumers,
  setShowDevices,
  setShowConsumers,
  filterCategories,
  activeFilters,
  onFilterChange,
  onClearFilters
}) => {
  const hasActiveFilters = Object.values(activeFilters).some(values => values.length > 0);
  const totalActiveFilters = Object.values(activeFilters).reduce((sum, values) => sum + values.length, 0);
  
  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-indigo-950 border-0 shadow-md">
      <CardContent className="p-4">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          {/* Map controls */}
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
              <Button
                size="sm"
                variant={showDevices ? "default" : "outline"}
                className={cn(
                  "rounded-md h-8",
                  showDevices ? "bg-blue-600 text-white hover:bg-blue-700" : ""
                )}
                onClick={() => setShowDevices(!showDevices)}
              >
                <Map className="h-4 w-4 mr-1" />
                Devices
                <Badge variant="outline" className="ml-1 bg-white text-blue-600 dark:bg-gray-700">
                  {filteredDevicesCount}
                </Badge>
              </Button>
              
              <Button
                size="sm"
                variant={showConsumers ? "default" : "outline"}
                className={cn(
                  "rounded-md h-8 ml-1",
                  showConsumers ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""
                )}
                onClick={() => setShowConsumers(!showConsumers)}
              >
                Consumers
                <Badge variant="outline" className="ml-1 bg-white text-emerald-600 dark:bg-gray-700">
                  {consumersCount}
                </Badge>
              </Button>
            </div>
          </div>
          
          {/* Filter tags - now horizontal */}
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(activeFilters).map(([category, values]) =>
              values.map(value => {
                // Find the label for this value from filterCategories
                const categoryObj = filterCategories.find(c => c.name === category);
                const optionObj = categoryObj?.options.find(o => o.value === value);
                const label = optionObj?.label || value;
                
                return (
                  <Badge
                    key={`${category}-${value}`}
                    className="h-8 px-3 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900 cursor-pointer flex items-center gap-1"
                    onClick={() => onFilterChange(category, value)}
                  >
                    {category}: {label}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                );
              })
            )}
            
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="h-8 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear all
              </Button>
            )}
            
            {/* Filter dropdown - now positioned for horizontal layout */}
            <div className="relative inline-block">
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 bg-white dark:bg-gray-800 border-dashed flex items-center gap-1",
                  hasActiveFilters ? "border-blue-500 text-blue-600" : ""
                )}
                onClick={() => {
                  const filterModal = document.getElementById('filter-modal');
                  if (filterModal) {
                    filterModal.classList.toggle('hidden');
                  }
                }}
              >
                <FilterIcon className="h-3.5 w-3.5" />
                <span>Filters</span>
                {totalActiveFilters > 0 && (
                  <Badge 
                    variant="secondary" 
                    className="ml-1 rounded-full px-1.5 py-0 text-xs bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300"
                  >
                    {totalActiveFilters}
                  </Badge>
                )}
              </Button>
              
              {/* Filter modal - now positioned for horizontal layout */}
              <div id="filter-modal" className="hidden absolute top-full right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                <div className="p-3">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium text-sm">Filter Options</h3>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto">
                    {filterCategories.map(category => (
                      <div key={category.name} className="mb-2">
                        <h4 className="text-xs font-medium uppercase text-gray-500 mb-1">{category.name}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {category.options.map(option => (
                            <div 
                              key={option.value}
                              className="flex items-center p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                              onClick={() => onFilterChange(category.name, option.value)}
                            >
                              <div className={cn(
                                "w-3 h-3 rounded-full mr-2",
                                activeFilters[category.name]?.includes(option.value) 
                                  ? "bg-blue-500" 
                                  : "bg-gray-200 dark:bg-gray-600"
                              )} />
                              <span className="text-sm truncate">{option.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AestheticFilter;
